import { useCallback, useRef, useState } from 'react';
import {
    useGetAndProcessTectlyResultsMutation,
    useLazyGetTectlyFloorStatusQuery,
    useUploadTectlyFloorMutation,
} from 'src/api/v2/tectly-v2-ApiSlice';
import { FloorRecognitionProcessingStatus, TectlyFloorStatusResponse } from 'src/model/api/v2/data-contracts';
import {
    buildInitialChecklist,
    CHECKLIST_ORDER,
    ChecklistItemStatus,
    ChecklistKey,
    ChecklistState,
    RecognitionContext,
    RecognitionOutcome,
    RecognitionResult,
} from 'src/pages/PlannerPage/RoomRecognition/recognitionTypes';

// Mirrors ErrorCodes::TECTLY_LIMIT_EXCEEDED (common/ErrorCodes.php) Backend error codes are not yet exposed to the UI as generated types, so this must be kept in sync manually
const TECTLY_LIMIT_EXCEEDED = 'TECTLY_LIMIT_EXCEEDED';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60;
const MIN_ITEM_DELAY_MS = 1000;
const HIDE_DELAY_MS = 1000;
const MOCK_FLOOR_PREFIX = 'demo-floor-mock-';

type StatusValue = FloorRecognitionProcessingStatus | undefined;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function toItemStatus(status: StatusValue): ChecklistItemStatus {
    return status === FloorRecognitionProcessingStatus.SUCCESS ? ChecklistItemStatus.SUCCESS : ChecklistItemStatus.FAILED;
}

function isComplete(status: TectlyFloorStatusResponse): boolean {
    const values = [
        status.horizontalScaleProcessingStatus,
        status.wallProcessingStatus,
        status.roomProcessingStatus,
        status.wallOpeningProcessingStatus,
        status.postProcessingStatus,
    ];
    return values.every((value) => value != null && value !== FloorRecognitionProcessingStatus.PENDING);
}

function resolveOutcome(measurementPositive: boolean, roomStatus: StatusValue): RecognitionResult {
    const roomsDetected = roomStatus === FloorRecognitionProcessingStatus.SUCCESS;
    if (!measurementPositive && roomStatus === FloorRecognitionProcessingStatus.FAILED) {
        return { outcome: RecognitionOutcome.UNSUCCESSFUL, roomsDetected };
    }
    if (measurementPositive) {
        return { outcome: RecognitionOutcome.MEASUREMENT_DETECTED, roomsDetected };
    }
    return { outcome: RecognitionOutcome.NO_MEASUREMENT, roomsDetected };
}

export function useRoomRecognition() {
    const [uploadFloor] = useUploadTectlyFloorMutation();
    const [getStatus] = useLazyGetTectlyFloorStatusQuery();
    const [processResults] = useGetAndProcessTectlyResultsMutation();

    const [checklist, setChecklist] = useState<ChecklistState>(buildInitialChecklist);
    const [isProcessing, setIsProcessing] = useState(false);
    const [result, setResult] = useState<RecognitionResult | null>(null);

    const cancelledRef = useRef(false);

    const setItem = useCallback((key: ChecklistKey, status: ChecklistItemStatus) => {
        setChecklist((prev) => ({ ...prev, [key]: status }));
    }, []);

    const reset = useCallback(() => {
        cancelledRef.current = false;
        setChecklist(buildInitialChecklist());
        setResult(null);
    }, []);

    const clearResult = useCallback(() => setResult(null), []);

    const runPolling = useCallback(
        async (tectlyFloorId: string): Promise<RecognitionResult> => {
            const mockMode = tectlyFloorId.startsWith(MOCK_FLOOR_PREFIX);
            const startTime = Date.now();
            const pending: Partial<Record<ChecklistKey, FloorRecognitionProcessingStatus>> = {};
            let lastShownIndex = -1;

            const minTimeForIndex = (index: number) => (index + 1) * MIN_ITEM_DELAY_MS;

            const revealReadyItems = (excludeLast: boolean) => {
                const elapsed = Date.now() - startTime;
                const maxIndex = excludeLast ? CHECKLIST_ORDER.length - 2 : CHECKLIST_ORDER.length - 1;
                for (let i = lastShownIndex + 1; i <= maxIndex; i++) {
                    const key = CHECKLIST_ORDER[i];
                    if (elapsed >= minTimeForIndex(i) && pending[key] !== undefined) {
                        setItem(key, toItemStatus(pending[key]));
                        lastShownIndex = i;
                    } else {
                        return;
                    }
                }
            };

            const revealLastItem = () => {
                const key = CHECKLIST_ORDER[CHECKLIST_ORDER.length - 1];
                setItem(key, toItemStatus(pending[key]));
                lastShownIndex = CHECKLIST_ORDER.length - 1;
            };

            const allShownExceptLast = () => lastShownIndex >= CHECKLIST_ORDER.length - 2;

            const collectStatuses = (status: TectlyFloorStatusResponse) => {
                const statusMap: Record<ChecklistKey, StatusValue> = {
                    [ChecklistKey.MEASUREMENT]: status.horizontalScaleProcessingStatus,
                    [ChecklistKey.OUTER_WALL]: status.wallProcessingStatus,
                    [ChecklistKey.ROOMS]: status.roomProcessingStatus,
                    [ChecklistKey.DOORS]: status.wallOpeningProcessingStatus,
                    [ChecklistKey.WINDOWS]: status.wallOpeningProcessingStatus,
                };
                CHECKLIST_ORDER.forEach((key) => {
                    const value = statusMap[key];
                    if (value && value !== FloorRecognitionProcessingStatus.PENDING) {
                        pending[key] = value;
                    }
                });
            };

            const finish = async (status: TectlyFloorStatusResponse): Promise<RecognitionResult> => {
                const measurementPositive = status.horizontalScaleProcessingStatus === FloorRecognitionProcessingStatus.SUCCESS;
                setItem(CHECKLIST_ORDER[CHECKLIST_ORDER.length - 1], ChecklistItemStatus.IN_PROGRESS);
                const processed = await processResults({ tectlyFloorId, mockMode }).unwrap().catch(() => null);
                revealLastItem();
                if (!processed?.success) {
                    return { outcome: RecognitionOutcome.UNSUCCESSFUL, roomsDetected: false };
                }
                return resolveOutcome(measurementPositive, status.roomProcessingStatus);
            };

            for (let attempts = 0; attempts < MAX_POLL_ATTEMPTS; attempts++) {
                if (cancelledRef.current) throw new Error('cancelled');
                const status = await getStatus({ tectlyFloorId, mockMode }).unwrap().catch(() => null);

                if (status && status.success) {
                    collectStatuses(status);
                    revealReadyItems(true);

                    if (isComplete(status)) {
                        while (!allShownExceptLast()) {
                            await delay(100);
                            revealReadyItems(true);
                        }
                        return finish(status);
                    }
                }

                await delay(POLL_INTERVAL_MS);
            }

            // Timed out: process whatever the last response allows, revealing the rest as failed.
            const last = await getStatus({ tectlyFloorId, mockMode }).unwrap().catch(() => null);
            if (last?.success) {
                collectStatuses(last);
            }
            for (let i = lastShownIndex + 1; i <= CHECKLIST_ORDER.length - 2; i++) {
                setItem(CHECKLIST_ORDER[i], toItemStatus(pending[CHECKLIST_ORDER[i]]));
            }
            lastShownIndex = CHECKLIST_ORDER.length - 2;
            const fallback = last ?? ({ success: false } as TectlyFloorStatusResponse);
            return finish(fallback);
        },
        [getStatus, processResults, setItem],
    );

    const runSimulation = useCallback(async (): Promise<RecognitionResult> => {
        for (const key of CHECKLIST_ORDER) {
            await delay(MIN_ITEM_DELAY_MS);
            if (cancelledRef.current) throw new Error('cancelled');
            setItem(key, ChecklistItemStatus.FAILED);
        }
        return { outcome: RecognitionOutcome.UNSUCCESSFUL, roomsDetected: false };
    }, [setItem]);

    const start = useCallback(
        async (context: RecognitionContext) => {
            reset();
            setIsProcessing(true);

            let recognitionResult: RecognitionResult;
            try {
                const upload = await uploadFloor({
                    projectId: context.projectId,
                    floorId: context.floorId,
                    mockMode: context.isSampleImage,
                }).unwrap().catch(() => null);

                if (upload && !upload.success && upload.errorCode === TECTLY_LIMIT_EXCEEDED) {
                    setIsProcessing(false);
                    setResult({ outcome: RecognitionOutcome.LIMIT_EXCEEDED, roomsDetected: false });
                    return;
                }

                const tectlyFloorId = upload?.success ? upload.tectlyFloorId : undefined;
                recognitionResult = tectlyFloorId ? await runPolling(tectlyFloorId) : await runSimulation();
            } catch {
                if (cancelledRef.current) return;
                recognitionResult = { outcome: RecognitionOutcome.UNSUCCESSFUL, roomsDetected: false };
            }

            await delay(HIDE_DELAY_MS);
            if (cancelledRef.current) return;
            setIsProcessing(false);
            setResult(recognitionResult);
        },
        [reset, uploadFloor, runPolling, runSimulation],
    );

    const cancel = useCallback(() => {
        cancelledRef.current = true;
        setIsProcessing(false);
        setResult(null);
    }, []);

    return { checklist, isProcessing, result, start, cancel, clearResult };
}
